import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, MapPin, DollarSign, Eye, ShoppingCart, Calendar, ArrowUp, ArrowDown, Minus, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';

const SixtLocationPricing = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('14d');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sortBy, setSortBy] = useState('conversion_trend');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Helper function to generate daily data for last 14 days
  const generateDailyData = (basePrice, baseViews, baseBookings, trendType) => {
    const dailyData = [];
    const today = new Date();
    
    for (let i = 13; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Simulate realistic variations
      let priceVariation = 1;
      let viewsVariation = 1;
      let bookingsVariation = 1;
      
      // Weekend effect (Friday, Saturday have higher prices and views)
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 5 || dayOfWeek === 6) {
        priceVariation = 1.15;
        viewsVariation = 1.3;
        bookingsVariation = 1.25;
      }
      
      // Trend effect based on trendType
      let trendFactor = 1;
      if (trendType === 'increasing') {
        trendFactor = 0.85 + (0.3 * (13 - i) / 13); // Gradual increase over 14 days
      } else if (trendType === 'decreasing') {
        trendFactor = 1.15 - (0.3 * (13 - i) / 13); // Gradual decrease over 14 days
      } else {
        // Stable with minor random variations
        trendFactor = 0.95 + Math.random() * 0.1;
      }
      
      // Random daily variations
      const randomVariation = 0.9 + Math.random() * 0.2;
      
      const dailyPrice = Math.round(basePrice * priceVariation * (0.95 + Math.random() * 0.1));
      const dailyViews = Math.round(baseViews * viewsVariation * trendFactor * randomVariation);
      const dailyBookings = Math.round(baseBookings * bookingsVariation * trendFactor * randomVariation);
      const conversionRate = dailyViews > 0 ? (dailyBookings / dailyViews) * 100 : 0;
      
      dailyData.push({
        date: date.toISOString().split('T')[0],
        price: dailyPrice,
        views: dailyViews,
        bookings: dailyBookings,
        conversion_rate: Math.round(conversionRate * 100) / 100,
        revenue: dailyBookings * dailyPrice,
        day_name: date.toLocaleDateString('tr-TR', { weekday: 'short' })
      });
    }
    
    return dailyData;
  };

  // Dummy location data with pricing metrics and daily breakdown
  const locationData = [
    {
      id: 1,
      name: 'Istanbul Atatürk Airport',
      region: 'Istanbul',
      current_price: 450,
      currency: 'TL',
      last_7_days: {
        views: 12500,
        bookings: 420,
        conversion_rate: 3.36,
        revenue: 189000
      },
      last_2_days: {
        views: 3200,
        bookings: 128,
        conversion_rate: 4.0,
        revenue: 57600
      },
      previous_5_days: {
        views: 9300,
        bookings: 292,
        conversion_rate: 3.14,
        revenue: 131400
      },
      trend: 'increasing',
      price_recommendation: 'increase',
      suggested_price: 485,
      confidence: 0.89,
      daily_breakdown: generateDailyData(450, 890, 30, 'increasing')
    },
    {
      id: 2,
      name: 'Ankara Esenboğa Airport',
      region: 'Ankara',
      current_price: 380,
      currency: 'TL',
      last_7_days: {
        views: 8900,
        bookings: 245,
        conversion_rate: 2.75,
        revenue: 93100
      },
      last_2_days: {
        views: 2100,
        bookings: 38,
        conversion_rate: 1.81,
        revenue: 14440
      },
      previous_5_days: {
        views: 6800,
        bookings: 207,
        conversion_rate: 3.04,
        revenue: 78660
      },
      trend: 'decreasing',
      price_recommendation: 'decrease',
      suggested_price: 350,
      confidence: 0.76,
      daily_breakdown: generateDailyData(380, 635, 17, 'decreasing')
    },
    {
      id: 3,
      name: 'İzmir Adnan Menderes',
      region: 'İzmir',
      current_price: 420,
      currency: 'TL',
      last_7_days: {
        views: 9800,
        bookings: 310,
        conversion_rate: 3.16,
        revenue: 130200
      },
      last_2_days: {
        views: 2800,
        bookings: 95,
        conversion_rate: 3.39,
        revenue: 39900
      },
      previous_5_days: {
        views: 7000,
        bookings: 215,
        conversion_rate: 3.07,
        revenue: 90300
      },
      trend: 'stable',
      price_recommendation: 'maintain',
      suggested_price: 420,
      confidence: 0.65,
      daily_breakdown: generateDailyData(420, 700, 22, 'stable')
    },
    {
      id: 4,
      name: 'Antalya Airport',
      region: 'Antalya',
      current_price: 520,
      currency: 'TL',
      last_7_days: {
        views: 15600,
        bookings: 680,
        conversion_rate: 4.36,
        revenue: 353600
      },
      last_2_days: {
        views: 4200,
        bookings: 210,
        conversion_rate: 5.0,
        revenue: 109200
      },
      previous_5_days: {
        views: 11400,
        bookings: 470,
        conversion_rate: 4.12,
        revenue: 244400
      },
      trend: 'increasing',
      price_recommendation: 'increase',
      suggested_price: 565,
      confidence: 0.94,
      daily_breakdown: generateDailyData(520, 1115, 49, 'increasing')
    },
    {
      id: 5,
      name: 'Bursa City Center',
      region: 'Bursa',
      current_price: 320,
      currency: 'TL',
      last_7_days: {
        views: 5400,
        bookings: 135,
        conversion_rate: 2.5,
        revenue: 43200
      },
      last_2_days: {
        views: 1400,
        bookings: 42,
        conversion_rate: 3.0,
        revenue: 13440
      },
      previous_5_days: {
        views: 4000,
        bookings: 93,
        conversion_rate: 2.33,
        revenue: 29760
      },
      trend: 'increasing',
      price_recommendation: 'increase',
      suggested_price: 340,
      confidence: 0.71,
      daily_breakdown: generateDailyData(320, 385, 10, 'increasing')
    },
    {
      id: 6,
      name: 'Trabzon Airport',
      region: 'Trabzon',
      current_price: 290,
      currency: 'TL',
      last_7_days: {
        views: 3200,
        bookings: 78,
        conversion_rate: 2.44,
        revenue: 22620
      },
      last_2_days: {
        views: 900,
        bookings: 15,
        conversion_rate: 1.67,
        revenue: 4350
      },
      previous_5_days: {
        views: 2300,
        bookings: 63,
        conversion_rate: 2.74,
        revenue: 18270
      },
      trend: 'decreasing',
      price_recommendation: 'decrease',
      suggested_price: 270,
      confidence: 0.68,
      daily_breakdown: generateDailyData(290, 230, 6, 'decreasing')
    }
  ];

  // Calculate trend indicators
  const getTrendIcon = (location) => {
    const trend = location.last_2_days.conversion_rate - location.previous_5_days.conversion_rate;
    if (trend > 0.3) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (trend < -0.3) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const getPriceChangeIcon = (recommendation) => {
    if (recommendation === 'increase') return <ArrowUp className="h-4 w-4 text-green-500" />;
    if (recommendation === 'decrease') return <ArrowDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const getPriceChangeColor = (recommendation) => {
    if (recommendation === 'increase') return 'text-green-600 bg-green-50';
    if (recommendation === 'decrease') return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  // Sort locations based on selected criteria
  const sortedLocations = [...locationData].sort((a, b) => {
    switch (sortBy) {
      case 'conversion_trend':
        const trendA = a.last_2_days.conversion_rate - a.previous_5_days.conversion_rate;
        const trendB = b.last_2_days.conversion_rate - b.previous_5_days.conversion_rate;
        return trendB - trendA;
      case 'revenue':
        return b.last_7_days.revenue - a.last_7_days.revenue;
      case 'conversion_rate':
        return b.last_2_days.conversion_rate - a.last_2_days.conversion_rate;
      case 'confidence':
        return b.confidence - a.confidence;
      default:
        return 0;
    }
  });

  const timeRanges = [
    { key: '7d', label: '7 Gün' },
    { key: '14d', label: '14 Gün' },
    { key: '30d', label: '30 Gün' }
  ];

  // Handle row expansion
  const toggleRowExpansion = (locationId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedRows(newExpanded);
  };

  // Get conversion rate trend for a day
  const getDailyTrendIcon = (currentCR, previousCR) => {
    const diff = currentCR - previousCR;
    if (diff > 0.2) return <TrendingUp className="h-3 w-3 text-green-500" />;
    if (diff < -0.2) return <TrendingDown className="h-3 w-3 text-red-500" />;
    return <Minus className="h-3 w-3 text-gray-400" />;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <DollarSign className="h-8 w-8 text-orange-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Lokasyon Fiyat Optimizasyonu</h1>
            <p className="text-gray-600">Günlük conversion rate trendlerine göre dinamik fiyat önerileri</p>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex space-x-4 mb-6">
          {timeRanges.map((range) => (
            <button
              key={range.key}
              onClick={() => setSelectedTimeframe(range.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedTimeframe === range.key
                  ? 'bg-orange-100 text-orange-700 border-2 border-orange-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Lokasyon</p>
              <p className="text-2xl font-bold text-gray-900">{locationData.length}</p>
            </div>
            <MapPin className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Fiyat Artış Önerisi</p>
              <p className="text-2xl font-bold text-green-600">
                {locationData.filter(l => l.price_recommendation === 'increase').length}
              </p>
            </div>
            <ArrowUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Fiyat Düşürme Önerisi</p>
              <p className="text-2xl font-bold text-red-600">
                {locationData.filter(l => l.price_recommendation === 'decrease').length}
              </p>
            </div>
            <ArrowDown className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ortalama CR</p>
              <p className="text-2xl font-bold text-gray-900">
                {(locationData.reduce((sum, l) => sum + l.last_7_days.conversion_rate, 0) / locationData.length).toFixed(2)}%
              </p>
            </div>
            <ShoppingCart className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-xl shadow-sm border mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Sırala:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="conversion_trend">CR Trendi</option>
              <option value="revenue">Gelir</option>
              <option value="conversion_rate">Mevcut CR</option>
              <option value="confidence">Güven Skoru</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Son güncelleme: {new Date().toLocaleDateString('tr-TR')}</span>
          </div>
        </div>
      </div>

      {/* Location Pricing Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lokasyon
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mevcut Fiyat
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CR Trendi (2 vs 5 gün)
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Görüntülenme/Rezervasyon
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fiyat Önerisi
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Güven Skoru
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlem
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedLocations.map((location) => (
                <React.Fragment key={location.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleRowExpansion(location.id)}
                          className="mr-2 p-1 hover:bg-gray-100 rounded"
                        >
                          {expandedRows.has(location.id) ? 
                            <ChevronUp className="h-4 w-4 text-gray-400" /> : 
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          }
                        </button>
                        <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{location.name}</div>
                          <div className="text-sm text-gray-500">{location.region}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {location.current_price} {location.currency}
                      </div>
                      <div className="text-sm text-gray-500">günlük</div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getTrendIcon(location)}
                        <div className="text-sm">
                          <span className="font-medium">
                            {location.last_2_days.conversion_rate.toFixed(2)}%
                          </span>
                          <span className="text-gray-500"> vs </span>
                          <span className="font-medium">
                            {location.previous_5_days.conversion_rate.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {((location.last_2_days.conversion_rate - location.previous_5_days.conversion_rate) * 100).toFixed(1)} puan fark
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center text-sm">
                          <Eye className="h-4 w-4 text-blue-500 mr-1" />
                          {location.last_7_days.views.toLocaleString()}
                        </div>
                        <div className="flex items-center text-sm">
                          <ShoppingCart className="h-4 w-4 text-green-500 mr-1" />
                          {location.last_7_days.bookings}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        7 günlük toplam
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriceChangeColor(location.price_recommendation)}`}>
                        {getPriceChangeIcon(location.price_recommendation)}
                        <span className="ml-1">
                          {location.suggested_price} {location.currency}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {location.price_recommendation === 'increase' ? '+' : location.price_recommendation === 'decrease' ? '' : ''}
                        {location.price_recommendation !== 'maintain' ? 
                          `${((location.suggested_price - location.current_price) / location.current_price * 100).toFixed(1)}%` : 
                          'Değişiklik yok'
                        }
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-orange-500 h-2 rounded-full" 
                            style={{ width: `${location.confidence * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {(location.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedLocation(location)}
                          className="text-orange-600 hover:text-orange-900 font-medium"
                        >
                          Detay
                        </button>
                        <button
                          onClick={() => toggleRowExpansion(location.id)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          {expandedRows.has(location.id) ? 'Gizle' : '14 Gün'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Daily Breakdown Row */}
                  {expandedRows.has(location.id) && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-gray-50">
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <BarChart3 className="h-4 w-4" />
                            <span>Son 14 Günlük Detaylı Analiz - {location.name}</span>
                          </div>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-3 font-medium text-gray-600">Tarih</th>
                                  <th className="text-left py-2 px-3 font-medium text-gray-600">Gün</th>
                                  <th className="text-right py-2 px-3 font-medium text-gray-600">Fiyat</th>
                                  <th className="text-right py-2 px-3 font-medium text-gray-600">Görüntülenme</th>
                                  <th className="text-right py-2 px-3 font-medium text-gray-600">Rezervasyon</th>
                                  <th className="text-right py-2 px-3 font-medium text-gray-600">CR %</th>
                                  <th className="text-right py-2 px-3 font-medium text-gray-600">Gelir</th>
                                  <th className="text-center py-2 px-3 font-medium text-gray-600">Trend</th>
                                </tr>
                              </thead>
                              <tbody>
                                {location.daily_breakdown.map((day, index) => {
                                  const previousDay = location.daily_breakdown[index - 1];
                                  const isWeekend = day.day_name === 'Cmt' || day.day_name === 'Paz';
                                  
                                  return (
                                    <tr key={day.date} className={`border-b border-gray-100 ${isWeekend ? 'bg-blue-25' : ''}`}>
                                      <td className="py-2 px-3 text-gray-900">
                                        {new Date(day.date).toLocaleDateString('tr-TR', { 
                                          day: '2-digit', 
                                          month: '2-digit' 
                                        })}
                                      </td>
                                      <td className="py-2 px-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          isWeekend ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                        }`}>
                                          {day.day_name}
                                        </span>
                                      </td>
                                      <td className="py-2 px-3 text-right font-medium text-gray-900">
                                        {day.price} ₺
                                      </td>
                                      <td className="py-2 px-3 text-right text-gray-900">
                                        {day.views.toLocaleString()}
                                      </td>
                                      <td className="py-2 px-3 text-right font-medium text-gray-900">
                                        {day.bookings}
                                      </td>
                                      <td className="py-2 px-3 text-right">
                                        <span className={`font-medium ${
                                          day.conversion_rate >= 4 ? 'text-green-600' :
                                          day.conversion_rate >= 2.5 ? 'text-orange-600' : 'text-red-600'
                                        }`}>
                                          {day.conversion_rate.toFixed(2)}%
                                        </span>
                                      </td>
                                      <td className="py-2 px-3 text-right font-medium text-gray-900">
                                        {day.revenue.toLocaleString()} ₺
                                      </td>
                                      <td className="py-2 px-3 text-center">
                                        {previousDay && getDailyTrendIcon(day.conversion_rate, previousDay.conversion_rate)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Daily Summary */}
                          <div className="grid grid-cols-4 gap-4 mt-4">
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-xs text-gray-500">En Yüksek CR</div>
                              <div className="font-semibold text-green-600">
                                {Math.max(...location.daily_breakdown.map(d => d.conversion_rate)).toFixed(2)}%
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-xs text-gray-500">En Düşük CR</div>
                              <div className="font-semibold text-red-600">
                                {Math.min(...location.daily_breakdown.map(d => d.conversion_rate)).toFixed(2)}%
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-xs text-gray-500">Ortalama Fiyat</div>
                              <div className="font-semibold text-gray-900">
                                {Math.round(location.daily_breakdown.reduce((sum, d) => sum + d.price, 0) / location.daily_breakdown.length)} ₺
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-xs text-gray-500">Toplam Gelir</div>
                              <div className="font-semibold text-gray-900">
                                {location.daily_breakdown.reduce((sum, d) => sum + d.revenue, 0).toLocaleString()} ₺
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Location Detail Modal - keeping existing modal */}
      {selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">{selectedLocation.name}</h3>
                <button
                  onClick={() => setSelectedLocation(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Mevcut Performans</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mevcut Fiyat:</span>
                      <span className="font-medium">{selectedLocation.current_price} {selectedLocation.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">7 Günlük CR:</span>
                      <span className="font-medium">{selectedLocation.last_7_days.conversion_rate.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">7 Günlük Gelir:</span>
                      <span className="font-medium">{selectedLocation.last_7_days.revenue.toLocaleString()} {selectedLocation.currency}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Trend Analizi</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Son 2 Gün CR:</span>
                      <span className="font-medium">{selectedLocation.last_2_days.conversion_rate.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Önceki 5 Gün CR:</span>
                      <span className="font-medium">{selectedLocation.previous_5_days.conversion_rate.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trend:</span>
                      <span className={`font-medium ${
                        selectedLocation.trend === 'increasing' ? 'text-green-600' : 
                        selectedLocation.trend === 'decreasing' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {selectedLocation.trend === 'increasing' ? '📈 Yükseliyor' : 
                         selectedLocation.trend === 'decreasing' ? '📉 Düşüyor' : '➡️ Sabit'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">Fiyat Önerisi</h4>
                <p className="text-orange-700">
                  {selectedLocation.price_recommendation === 'increase' && 
                    `Conversion rate artışı nedeniyle fiyatı ${selectedLocation.suggested_price} ${selectedLocation.currency}'ye çıkarmanızı öneriyoruz.`
                  }
                  {selectedLocation.price_recommendation === 'decrease' && 
                    `Conversion rate düşüşü nedeniyle fiyatı ${selectedLocation.suggested_price} ${selectedLocation.currency}'ye indirmenizi öneriyoruz.`
                  }
                  {selectedLocation.price_recommendation === 'maintain' && 
                    `Mevcut fiyat seviyesini korumanızı öneriyoruz. Performans stabil.`
                  }
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  Güven skoru: {(selectedLocation.confidence * 100).toFixed(0)}%
                </p>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => setSelectedLocation(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Kapat
                </button>
                <button
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Fiyatı Uygula
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SixtLocationPricing; 